import { createApiRef } from '@backstage/core-plugin-api';
import { AxiosInstance, AxiosResponse } from 'axios';
import { AxiosInstanceProviderApi } from './axiosInstanceApi';

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  const chunkSize = 0x8000;
  let binary = '';

  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize);
    binary += String.fromCharCode(...chunk);
  }

  return btoa(binary);
}

export interface PromotionImageApiResponse {
  imageBase64: string;
  contentType: string;
}

export interface PromotionApi {
  getPromotionData: () => Promise<any>;
  postAddPromotion: (payload: any) => Promise<AxiosResponse>;
  deletePromotion: (id: string, assetURL: string) => Promise<AxiosResponse>;
  getPromotionDataByIdTitle: (id: any, title: any) => Promise<AxiosResponse>;
  getPromotionImage: (
    promotionId: any,
    title: any,
  ) => Promise<PromotionImageApiResponse>;
  getPromotionDataByIdTitleUpdatePage: (
    promotionId: any,
    title: any,
  ) => Promise<AxiosResponse>;
  updatePromotionById: (
    payload: any,
    promotionId: any,
  ) => Promise<AxiosResponse>;
}

export const promotionApiRef = createApiRef<PromotionApi>({
  id: 'cbre.devx.api.promotions',
});

export class PromotionApiImpl implements PromotionApi {
  private readonly axiosInstancePromise: Promise<AxiosInstance>;

  constructor(axiosInstanceProviderApi: AxiosInstanceProviderApi) {
    this.axiosInstancePromise = axiosInstanceProviderApi.getInstance();
  }

  async getPromotionData() {
    const instance = await this.axiosInstancePromise;

    return instance
      .get('service/discovery/getPromotion')
      .then((res: any) => res.data);
  }

  async postAddPromotion(payload: any): Promise<AxiosResponse> {
    const formData = new FormData();
    let stringPayload = `{ 
      "orderID": "${payload.orderID}",
      "description": ${JSON.stringify(payload.description)},
      "templateType": "${payload.templateType}",
      "isActive": "${payload.isActive}",
      "relatedURL": "${payload.relatedUrl}",
      "title": "${payload.title}",
      "imageFileName": "${payload.imageFileName}",
      "imageBase64Format": "${payload.imageBase64Format}"
      }`;

    formData.append('payload', stringPayload);

    const config = {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    };

    const instance = await this.axiosInstancePromise;

    return instance.post('service/discovery/createPromotion', formData, config);
  }

  async deletePromotion(id: string, assetURL: string): Promise<AxiosResponse> {
    const instance = await this.axiosInstancePromise;

    return instance.delete('service/discovery/deletePromotion', {
      params: {
        promotionId: id,
        assetURL,
      },
    });
  }

  async getPromotionDataByIdTitle(id: any, title: any): Promise<AxiosResponse> {
    const instance = await this.axiosInstancePromise;

    return instance.get('service/discovery/getPromotion', {
      params: {
        promotionId: id,
        title,
      },
    });
  }

  async getPromotionImage(
    promotionId: any,
    title: any,
  ): Promise<PromotionImageApiResponse> {
    const instance = await this.axiosInstancePromise;

    return instance
      .get('service/discovery/promotions/image', {
        responseType: 'arraybuffer',
        params: {
          promotionId,
          title,
        },
      })
      .then(res => ({
        imageBase64:
          typeof res?.data === 'string'
            ? res.data
            : arrayBufferToBase64(res?.data as ArrayBuffer),
        contentType:
          res?.headers?.['content-type'] ||
          res?.headers?.['Content-Type'] ||
          'image/png',
      }));
  }

  async getPromotionDataByIdTitleUpdatePage(
    promotionId: any,
    title: any,
  ): Promise<AxiosResponse> {
    const instance = await this.axiosInstancePromise;

    return instance.get('service/discovery/getPromotion', {
      params: {
        promotionId,
        title,
      },
    });
  }

  async updatePromotionById(
    payload: any,
    promotionId: any,
  ): Promise<AxiosResponse> {
    const formData = new FormData();
    let stringPayload = `{ 
      "orderID": "${payload.orderID}",
      "description": ${JSON.stringify(payload.description)},
      "templateType": "${payload.templateType}",
      "isActive": "${payload.isActive}",
      "relatedURL": "${payload.relatedUrl}",
      "title": "${payload.title}",
      "imageFileName": "${payload.imageFileName}",
      "imageBase64Format": "${payload.imageBase64Format}"
      }`;

    formData.append('payload', stringPayload);

    const config = {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    };

    const instance = await this.axiosInstancePromise;

    return instance.put(
      `service/discovery/updatePromotion?promotionId=${promotionId}`,
      formData,
      config,
    );
  }
}
